import BaseWrapper from "../../../components/ui/BaseWrapper";
import dayjs from "dayjs";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import { useState, useEffect, useRef, useMemo } from "react";

import BaseTextField from "../../../components/ui/BaseTextField";

import { useNavigate, useSearchParams } from "react-router-dom";

import {
  deleteInvoice,
  fetchAllInvoices,
  fetchInvoiceStatusCounts,
} from "../../../services/api/invoice";
import { customerLabel, debounce, formatInvoiceStatus } from "../../../utils/helpers";
import { CurrencySymbol, InvoiceStatusType, ModuleType, UserType } from "../../../utils/enum";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import { showToast } from "../../../store/toast/toastSlice";
import CustomerInvoiceFilters from "./CustomerInvoiceFilters";
import { setInvoiceFilters } from "../../../store/filters/invoiceFilterSlice";
import FilterChip from "../../../components/FilterChip";
import BaseInvoiceStatus from "../../../components/ui/BaseInvoiceStatus";
import useAuth from "../../../hooks/useAuth";
import { renderUserCell } from "../../../components/ui/UserProfileLink";

const relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime);

const CustomerInvoicesTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getPermissionForModule, authRole } = useAuth();
  // GENERATED is the internal pre-verification stage. A customer never sees it —
  // the API withholds it from them — and to them a VERIFIED invoice is the one
  // they have "Received", so they get no chip for it either.
  const isCustomer = authRole === UserType.CUSTOMER;
  const recordLabel = customerLabel(authRole);
  const statusLabel = (status) => formatInvoiceStatus(status, authRole);
  const { hasUpdateAccess, hasDeleteAccess } = getPermissionForModule(ModuleType.CUSTOMER_INVOICE);

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const defaultFilterParam = searchParams.get("filter") || "";
  const defaultFilterArray = defaultFilterParam.split(",").filter(Boolean);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filters, setFilters] = useState(() => {
    // A customer has no Generated chip to land on, so their list opens on the
    // first stage they can actually see.
    const fallback = isCustomer ? ["VERIFIED"] : ["GENERATED"];
    if (defaultFilterArray.includes("GENERATED")) {
      return { status: fallback };
    } else if (defaultFilterArray.length > 0) {
      return { status: defaultFilterArray };
    } else {
      return { status: fallback };
    }
  });

  const [invoices, setInvoices] = useState([]);

  const invoiceFilters = useSelector((state) => state.invoiceFilters.customerInvoices);
  const { page, pageSize } = invoiceFilters.pagination;

  const [queryParams, setQueryParams] = useState({
    page: page + 1,
    limit: pageSize,
    status: filters.status ? [filters.status] : [],
    search: "",
  });

  const filtersPayload = useMemo(
    () => ({
      role: UserType.CLIENT,
      clientId: invoiceFilters.clientId || "",
      minTotal: invoiceFilters.minTotal || "",
      maxTotal: invoiceFilters.maxTotal || "",
      creationStartDate: invoiceFilters.creationStartDate || "",
      creationEndDate: invoiceFilters.creationEndDate || "",
      dueStartDate: invoiceFilters.dueStartDate || "",
      dueEndDate: invoiceFilters.dueEndDate || "",
      customer: invoiceFilters.customer?.value || undefined,
      generatedBy: invoiceFilters.generatedBy?.value || undefined,
      reviewedBy: invoiceFilters.reviewedBy?.value || undefined,
      sendBy: invoiceFilters.sendBy?.value || undefined,
      paidBy: invoiceFilters.paidBy?.value || undefined,
      tax: invoiceFilters.tax || "",
      id: invoiceFilters.id || "",
    }),
    [invoiceFilters],
  );
  const [searchQuery, setSearchQuery] = useState("");

  const {
    isPending: isFetchAllInvoicesApiLoading,
    data: invoicesData,
    refetch: refetchAllInvoices,
  } = useQuery({
    queryKey: ["invoices", queryParams],
    queryFn: () => fetchAllInvoices({ ...queryParams, ...filtersPayload }),
    keepPreviousData: true,
  });
  const { data: statusCountData, refetch: refetchStatusCounts } = useQuery({
    queryKey: ["invoiceStatusCounts", filtersPayload, "customerInvoices"],
    queryFn: () => fetchInvoiceStatusCounts(filtersPayload),
  });

  const {
    isPending: isDeleteInvoiceApiLoading,
    error: isDeleteInvoiceApiError,
    data: deletedInvoiceData,
    mutateAsync: deleteInvoiceMutation,
  } = useMutation({
    mutationFn: deleteInvoice,
    enabled: false,
  });
  useEffect(() => {
    setQueryParams({
      page: invoiceFilters.pagination.page + 1,
      limit: invoiceFilters.pagination.pageSize,
      search: invoiceFilters.search || "",
      role: UserType.CLIENT,
      clientId: invoiceFilters.clientId || undefined,
      status: filters.status ? [filters.status] : [],
      customer: invoiceFilters.customer?.value || undefined,
      generatedBy: invoiceFilters.generatedBy?.value || undefined,
      reviewedBy: invoiceFilters.reviewedBy?.value || undefined,
      sendBy: invoiceFilters.sendBy?.value || undefined,
      paidBy: invoiceFilters.paidBy?.value || undefined,
      creationStartDate: invoiceFilters.creationStartDate || undefined,
      creationEndDate: invoiceFilters.creationEndDate || undefined,
      creationEndDate: invoiceFilters.creationEndDate || undefined,
      dueStartDate: invoiceFilters.dueStartDate || undefined,
      dueEndDate: invoiceFilters.dueEndDate || undefined,
      minTotal: invoiceFilters.minTotal || undefined,
      maxTotal: invoiceFilters.maxTotal || undefined,
      tax: invoiceFilters.tax || "",
      id: invoiceFilters.id || "",
    });
  }, [invoiceFilters]);

  const updateQueryParamsDebounced = useRef(
    debounce((query) => {
      setQueryParams((prev) => ({
        ...prev,
        search: query.trim() || "",
        page: 1,
      }));
    }, 500),
  ).current;

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateQueryParamsDebounced(query);
  };

  const handlePaginationChange = (model) => {
    // setQueryParams((prev) => ({
    //   ...prev,
    //   page: model.page + 1, // data grid pages are 0-indexed
    //   limit: model.pageSize,
    // }));
    dispatch(
      setInvoiceFilters({
        module: "customerInvoices",
        filterName: "pagination",
        data: { page: model.page, pageSize: model.pageSize },
      }),
    );
  };

  const handleItemClick = (value, params) => {
    if (value === "EDIT") {
      navigate(`/invoices/edit/${params.id}?role=CLIENT`);
    }
    if (value === "DELETE") {
      handleDeleteInvoice(params?.id);
    }
  };

  const handleDeleteInvoice = async (id) => {
    try {
      await deleteInvoiceMutation(id);
      await refetchAllInvoices();
      dispatch(
        showToast({
          type: "success",
          message: `Invoice INV-#$${id}  successfully delete`,
        }),
      );
      refetchStatusCounts();
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.message || `Cannot delete at the moment`,
        }),
      );
    }
  };
  const invoicesWithUsers = invoicesData?.data?.invoices?.map((invoice) => {
    const getLatestByStatus = (status) =>
      [...invoice.statusHistory]
        .filter((h) => h.newStatus === status)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    return {
      ...invoice,
      reviewedBy: getLatestByStatus(InvoiceStatusType.VERIFIED)?.changedBy || null,
      sendBy: getLatestByStatus(InvoiceStatusType.UNPAID)?.changedBy || null,
      paidBy: getLatestByStatus(InvoiceStatusType.PAID)?.changedBy || null,
    };
  });

  const columns = [
    {
      field: "id",
      headerName: "Invoice ID",
      width: 150,
      renderCell: (params) => (
        <p
          className="cursor-pointer text-primary"
          onClick={() => navigate(`/invoices/edit/${params.value}?role=${UserType.CLIENT}`)}
        >
          INV-#{params.value}
        </p>
      ),
    },
    {
      field: "createdAt",
      headerName: "Date of creation",
      width: 150,
      renderCell: (params) => (
        <p>{params.value ? dayjs(params.value).format("DD MMM YYYY") : "-"}</p>
      ),
    },
    {
      field: "client",
      headerName: `${recordLabel} Details`,
      width: 150,
      renderCell: (params) => <p>{params.value?.clientName}</p>,
    },
    {
      field: "paymentTerms",
      headerName: "Payment Terms",
      width: 150,
      renderCell: (params) => <p>{`${params.value} Days`}</p>,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => <BaseInvoiceStatus status={params.value} isCustomer={isCustomer} />,
    },
    {
      field: "subTotal",
      headerName: "Sub Total",
      width: 150,
      renderCell: (params) => (
        <p>{CurrencySymbol[params.row.currency] + params.value?.toFixed(2)}</p>
      ),
    },
    {
      field: "taxPercentage",
      headerName: "Tax",
      width: 150,
      renderCell: (params) => {
        params.value && <p>{`${params.row.taxType} ${params.value}%`}</p>;
      },
    },
    {
      field: "grandTotal",
      headerName: "Grand Total",
      width: 150,
      renderCell: (params) => (
        <p> {CurrencySymbol[params.row.currency] + params.value?.toFixed(2)}</p>
      ),
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 150,
      renderCell: (params) => (
        <p> {params.value ? dayjs(params.value).format("DD MMM YYYY") : "-"}</p>
      ),
    },
    {
      field: "generatedBy",
      headerName: "Generated By",
      width: 150,
      renderCell: (params) => renderUserCell(params.value),
    },
    {
      field: "reviewedBy",
      headerName: "Approved By",
      width: 150,
      renderCell: (params) => renderUserCell(params.value),
    },
    {
      field: "sendBy",
      headerName: "Sent By",
      width: 150,
      renderCell: (params) => renderUserCell(params.value),
    },
    {
      field: "paidBy",
      headerName: "Paid By",
      width: 150,
      renderCell: (params) => renderUserCell(params.value),
    },

    {
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const workOrderCount = params?.row?._count?.invoiceWorkOrders;
        const menuOptions = [
          ...(hasUpdateAccess ? [{ label: "Edit", value: "EDIT" }] : []),
          ...(hasDeleteAccess ? [{ label: "Delete", value: "DELETE" }] : []),
        ];

        const filteredMenuOptions =
          workOrderCount > 0
            ? menuOptions.filter((item) => item.value === "EDIT")
            : menuOptions.filter((item) => item.value === "DELETE"); // when length > 0

        return (
          <BaseDropdownV2
            menuOptions={filteredMenuOptions}
            triggerElement={<i class="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(value) => handleItemClick(value, params?.row)}
          />
        );
      },
    },
  ];
  useEffect(() => {
    setInvoices(invoicesData?.data.invoices);
    applyFilter();
  }, [invoicesData]);
  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      page: page + 1,
      status: [...filters.status],
      search: "",
    }));
    setSearch("");

    applyFilter();
  }, [filters]);

  const normalizeStatusFromDrawer = (input) => {
    if (!input) return [""];

    if (typeof input === "object" && !Array.isArray(input) && "value" in input) {
      return [input.value];
    }

    if (Array.isArray(input) && typeof input[0] === "object" && "value" in input[0]) {
      return input.map((s) => s.value);
    }

    if (Array.isArray(input)) return input;

    if (typeof input === "string") return [input];

    return [""];
  };

  useEffect(() => {
    const filterStatus = invoiceFilters.status.value ? invoiceFilters.status : filters.status;
    const normalized = normalizeStatusFromDrawer(filterStatus);
    if (normalized.length > 0) {
      setFilters({ status: normalized });
    } else {
      setFilters({ status: [""] });
    }
  }, [invoiceFilters.status]);

  useEffect(() => {
    if (filtersChanged) {
      setFiltersChanged(false);
    }
  }, [filtersChanged]);

  const applyFilter = () => {
    const allInvoices = invoicesData?.data?.workOrders || [];

    if (!filters.status || filters.status.length === 0 || filters.status.includes("")) {
      setInvoices(allInvoices);
      return;
    }

    const filteredInvoices = allInvoices.filter((wo) => filters.status.includes(wo.status));

    setInvoices(filteredInvoices);
  };
  const normalizeStatus = (input) => {
    if (Array.isArray(input)) return input;
    if (typeof input === "string") return [input];
    return [""];
  };

  const handleFilterClick = (status) => {
    let statusArray = normalizeStatus(status);

    setFilters({ status: statusArray });

    const queryString = statusArray.filter(Boolean).join(",");
    setSearchParams({ filter: queryString });

    dispatch(
      setInvoiceFilters({
        module: "customerInvoices",
        filterName: "status",
        data: statusArray,
      }),
    );
    dispatch(
      setInvoiceFilters({
        module: "customerInvoices",
        filterName: "appliedFromDrawer",
        data: false,
      }),
    );
  };

  const appliedFilters = useMemo(() => {
    const f = invoiceFilters;
    const arr = [];
    if (f.id) arr.push({ prefix: "Invoice ID", key: "id", value: f.id });
    if (f.search) arr.push({ prefix: "Search", key: "search", value: f.search });

    if (f?.creationStartDate || f?.creationEndDate)
      arr.push({
        prefix: "Creation Start",
        key: "creationDateRange",
        value: `${f.creationStartDate || "Any"} - ${f.creationEndDate || "Any"}`,
      });
    if (f.dueStartDate || f.dueEndDate)
      arr.push({
        prefix: "Due Start",
        key: "dueDateRange",
        value: `${f.dueStartDate || "Any"} - ${f.dueEndDate || "Any"}`,
      });
    if (f.customer?.value)
      arr.push({
        prefix: recordLabel,
        key: "customer",
        value: f.customer.label,
      });
    if (f.generatedBy?.value)
      arr.push({
        prefix: "Generated By",
        key: "generatedBy",
        value: f.generatedBy.label,
      });

    if (f.reviewedBy?.value)
      arr.push({
        prefix: "Approved By",
        key: "reviewedBy",
        value: f.reviewedBy.label,
      });

    if (f.sendBy?.value) arr.push({ prefix: "Sent By", key: "sendBy", value: f.sendBy.label });

    if (f.paidBy?.value) arr.push({ prefix: "Paid By", key: "paidBy", value: f.paidBy.label });

    if (f.status?.value) arr.push({ prefix: "Status", key: "status", value: f.status.label });

    if (f.tax) arr.push({ prefix: "Tax", key: "tax", value: f.tax });

    return arr;
  }, [invoiceFilters]);

  const statusCounts = statusCountData?.statusCounts || {};
  const totalWorkOrdersCount = statusCountData?.totalCount || 0;
  const rowCount = search
    ? invoicesData?.data?.totalCount || 0
    : invoiceFilters?.appliedFromDrawer
      ? statusCounts[filters.status] || 0
      : filters.status?.[0]
        ? filters.status[0] === "APPROVED"
          ? invoicesData?.data?.totalCount || 0
          : statusCounts[filters.status[0]] || 0
        : totalWorkOrdersCount;

  const handleRemoveFilter = (key) => {
    if (key === "dueDateRange") {
      dispatch(
        setInvoiceFilters({
          module: "customerInvoices",
          filterName: "dueStartDate",
          data: "",
        }),
      );
      dispatch(
        setInvoiceFilters({
          module: "customerInvoices",
          filterName: "dueEndDate",
          data: "",
        }),
      );
    } else if (key === "creationDateRange") {
      dispatch(
        setInvoiceFilters({
          module: "customerInvoices",
          filterName: "creationStartDate",
          data: "",
        }),
      );
      dispatch(
        setInvoiceFilters({
          module: "customerInvoices",
          filterName: "creationEndDate",
          data: "",
        }),
      );
    } else {
      dispatch(
        setInvoiceFilters({
          module: "customerInvoices",
          filterName: key,
          data: "",
        }),
      );

      dispatch(
        setInvoiceFilters({
          module: "customerInvoices",
          filterName: "pagination",
          data: { page: 0, pageSize: invoiceFilters.pagination.pageSize },
        }),
      );
    }
    refetchAllInvoices();
    setFiltersChanged(true);
  };

  return (
    <BaseWrapper>
      <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
        <div className="flex flex-wrap items-center gap-3">
          <div
            className="flex items-center border-r border-[#8C8C8C] pr-5 cursor-pointer"
            onClick={() => setIsFilterDrawerOpen(true)}
          >
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
              <i className="fa-light fa-filter text-white text-sm"></i>
            </span>
            <p className="text-primary ml-2 cursor-pointer">Add Filter</p>
          </div>

          <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
            <div className="flex gap-2">
              {appliedFilters.map((f) => {
                return (
                  <div
                    key={(f.key || "") + (f.value || "") + (f.id || "")}
                    className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                  >
                    <span className="text-primary">{f.prefix}:</span>&nbsp;
                    <p className="text-primary text-sm mx-1">{f.value}</p>
                    <button
                      onClick={() => handleRemoveFilter(f.key, f.id)}
                      className="ml-2 text-primary hover:text-red-500"
                      aria-label={`Remove ${f.prefix} ${f.value}`}
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <BaseTextField
              placeholder="Search invoices..."
              value={searchQuery}
              onChange={handleSearchChange}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
              className="w-full sm:w-[300px] lg:w-[350px]"
            />
          </div>
        </div>
      </div>
      <div className=" my-4 flex overflow-x-auto [&::-webkit-scrollbar]:h-2">
        {!isCustomer && (
          <div className="mr-5">
            <FilterChip
              filter="Generated"
              count={
                search && filters.status.includes("GENERATED")
                  ? invoicesData?.data?.totalCount
                  : statusCounts.GENERATED || 0
              }
              active={filters.status.includes("GENERATED") ? true : false}
              onClick={() => handleFilterClick(["GENERATED"])}
            />
          </div>
        )}
        <div className="mr-5">
          <FilterChip
            filter={statusLabel("VERIFIED")}
            count={
              search && filters.status.includes("VERIFIED")
                ? invoicesData?.data?.totalCount
                : statusCounts.VERIFIED || 0
            }
            active={filters.status.includes("VERIFIED") ? true : false}
            onClick={() => handleFilterClick(["VERIFIED"])}
          />
        </div>

        <div className="mr-5">
          <FilterChip
            filter={statusLabel("UNPAID")}
            count={
              search && filters.status.includes("UNPAID")
                ? invoicesData?.data?.totalCount
                : statusCounts.UNPAID || 0
            }
            active={filters.status.includes("UNPAID") ? true : false}
            onClick={() => handleFilterClick(["UNPAID"])}
          />
        </div>
        <div className="mr-5">
          <FilterChip
            filter={statusLabel("OVERDUE")}
            count={
              search && filters.status.includes("OVERDUE")
                ? invoicesData?.data?.totalCount
                : statusCounts?.OVERDUE || 0 || 0
            }
            active={filters.status.includes("OVERDUE") ? true : false}
            onClick={() => handleFilterClick(["OVERDUE"])}
          />
        </div>
        <div className="mr-5">
          <FilterChip
            filter={statusLabel("PAID")}
            count={
              search && filters.status.includes("PAID")
                ? invoicesData?.data?.totalCount
                : statusCounts.PAID || 0
            }
            active={filters.status.includes("PAID") ? true : false}
            onClick={() => handleFilterClick(["PAID"])}
          />
        </div>
      </div>

      <div className="mt-2">
        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)]"
          rowHeight={38}
          columns={columns}
          data={invoicesWithUsers || []}
          isLoading={isFetchAllInvoicesApiLoading}
          disableColumnResize={false}
          disableRowSelectionOnClick
          checkboxSelection
          hideFooter={false}
          pagination
          paginationMode="server"
          rowCount={rowCount || 0}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{
            page: queryParams.page - 1,
            pageSize: queryParams.limit,
          }}
          onPaginationModelChange={handlePaginationChange}
        />
      </div>
      {isFilterDrawerOpen && (
        <CustomerInvoiceFilters
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          fetchCustomerInvoices={refetchAllInvoices}
          refetchStatusCounts={refetchStatusCounts}
          isLoading={isFetchAllInvoicesApiLoading}
          setFiltersChanged={setFiltersChanged}
        />
      )}
    </BaseWrapper>
  );
};

export default CustomerInvoicesTable;
